import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Clock, AlertCircle, Zap, CheckCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Scenario {
  id: string;
  title: string;
  icon: React.ElementType;
  before: {
    time: string;
    pain: string[];
    mood: string;
  };
  after: {
    time: string;
    benefits: string[];
    mood: string;
  };
}

const scenarios: Scenario[] = [
  {
    id: 'email',
    title: 'Writing Emails',
    icon: Clock,
    before: {
      time: '15-20 min',
      pain: [
        'Constant typos and corrections',
        'Losing train of thought while typing',
        'Hand cramps from repetitive strain',
        'Switching between mouse and keyboard',
      ],
      mood: '😫',
    },
    after: {
      time: '3-5 min',
      benefits: [
        'Speak naturally at conversation speed',
        'Perfect punctuation with voice commands',
        'Hands-free multitasking',
        'More thoughtful, flowing content',
      ],
      mood: '😊',
    },
  },
  {
    id: 'coding',
    title: 'Writing Code & Docs',
    icon: Zap,
    before: {
      time: '2-3 hours',
      pain: [
        'Wrist pain from constant typing',
        'Slow documentation writing',
        'Context switching fatigue',
        'Repetitive code patterns',
      ],
      mood: '😩',
    },
    after: {
      time: '45 min',
      benefits: [
        'Code by describing logic',
        'Instant documentation',
        'Custom code snippets by voice',
        'Healthier work posture',
      ],
      mood: '🚀',
    },
  },
  {
    id: 'content',
    title: 'Content Creation',
    icon: TrendingUp,
    before: {
      time: '4-5 hours',
      pain: [
        'Writer\'s block from slow typing',
        'Lost creative flow',
        'Physical exhaustion',
        'Multiple draft rewrites',
      ],
      mood: '😔',
    },
    after: {
      time: '1-2 hours',
      benefits: [
        'Natural storytelling flow',
        'Walk and create content',
        'Faster ideation to publication',
        'More content in less time',
      ],
      mood: '🎯',
    },
  },
];

export function BeforeAfter() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);

  const currentScenario = scenarios[activeScenario];

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute top-1/2 left-0 w-1/2 h-full bg-red-500/5 blur-3xl" />
        <div className="absolute top-1/2 right-0 w-1/2 h-full bg-green-500/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <ArrowRight className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">The Transformation</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Your Life, <span className="text-red-500">Before</span> and{' '}
              <span className="text-green-500">After</span> SpeakFlow
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See the dramatic difference in your daily workflow. Real scenarios, real impact.
            </p>
          </div>

          {/* Scenario Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {scenarios.map((scenario, index) => (
              <button
                key={scenario.id}
                onClick={() => setActiveScenario(index)}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-lg transition-all",
                  activeScenario === index
                    ? "bg-primary text-primary-foreground shadow-lg"
                    : "bg-card hover:bg-accent"
                )}
              >
                <scenario.icon className="h-5 w-5" />
                <span className="font-medium">{scenario.title}</span>
              </button>
            ))}
          </div>

          {/* Comparison View */}
          <div className="relative max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Before */}
              <Card className={cn(
                "border-red-500/20 bg-red-500/5",
                "animate-slide-up opacity-0"
              )} style={{ animationFillMode: 'forwards' }}>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-red-500">Before</h3>
                      <span className="text-4xl">{currentScenario.before.mood}</span>
                    </div>

                    <div className="flex items-center gap-2 text-red-500">
                      <Clock className="h-5 w-5" />
                      <span className="font-semibold">Time: {currentScenario.before.time}</span>
                    </div>

                    <div className="space-y-3">
                      {currentScenario.before.pain.map((pain, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                          <span className="text-sm">{pain}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground italic">
                        "I spend more time fighting with my keyboard than actually creating..."
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* After */}
              <Card className={cn(
                "border-green-500/20 bg-green-500/5",
                "animate-slide-up opacity-0"
              )} style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-green-500">After</h3>
                      <span className="text-4xl">{currentScenario.after.mood}</span>
                    </div>

                    <div className="flex items-center gap-2 text-green-500">
                      <Zap className="h-5 w-5" />
                      <span className="font-semibold">Time: {currentScenario.after.time}</span>
                    </div>

                    <div className="space-y-3">
                      {currentScenario.after.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground italic">
                        "I can finally work at the speed of thought!"
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Visual Comparison Slider */}
            <div className="mt-12 relative">
              <div className="h-24 relative rounded-lg overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-green-500/20" />
                <div 
                  className="absolute inset-y-0 left-0 bg-red-500/30 transition-all duration-300"
                  style={{ width: `${100 - sliderPosition}%` }}
                />
                <div 
                  className="absolute inset-y-0 right-0 bg-green-500/30 transition-all duration-300"
                  style={{ width: `${sliderPosition}%` }}
                />
                
                {/* Slider Handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-white cursor-grab active:cursor-grabbing"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                  onMouseDown={(e) => {
                    const startX = e.clientX;
                    const startPos = sliderPosition;
                    
                    const handleMouseMove = (e: MouseEvent) => {
                      const rect = e.currentTarget?.parentElement?.getBoundingClientRect();
                      if (rect) {
                        const x = e.clientX - rect.left;
                        const percentage = (x / rect.width) * 100;
                        setSliderPosition(Math.max(0, Math.min(100, percentage)));
                      }
                    };
                    
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between mt-2 text-sm font-medium">
                <span className="text-red-500">Struggling</span>
                <span className="text-green-500">Thriving</span>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <p className="text-3xl font-bold text-primary">75%</p>
                <p className="text-sm text-muted-foreground">Less Time</p>
              </div>
              <div className="space-y-2">
                <p className="text-3xl font-bold text-primary">3x</p>
                <p className="text-sm text-muted-foreground">More Output</p>
              </div>
              <div className="space-y-2">
                <p className="text-3xl font-bold text-primary">0%</p>
                <p className="text-sm text-muted-foreground">Hand Pain</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}